import { sanitizeHtmlValue } from '@/lib/element-schema-sanitizers'
import { sectionSchemaRegistry } from '@/lib/section-schema-registry'
import { cn } from '@/lib/utils'
import useLocale from '@/hooks/use-locale'

import { Button } from '@/components/ui/button'

import ButtonRegister from '../button-register'
import { Shell } from '../common/shell'

import type { SectionElementSchema } from '@/types/element-schema.type'

// Element schema for Puck editor
export const consultationFormSchema: SectionElementSchema = {
  componentType: 'ConsultationForm',
  label: 'Consultation Form',
  category: 'forms',
  description: 'Consultation/contact form section',
  elements: [
    {
      key: 'heading',
      type: 'textarea',
      label: 'Heading',
      helpText: 'Section heading (supports HTML)',
      default: '',
    },
    {
      key: 'headingColor',
      type: 'color',
      label: 'Heading Color',
      helpText: 'Leave empty for default white',
      default: '',
    },
    {
      key: 'bgGradientFrom',
      type: 'color',
      label: 'Background Gradient Start',
      default: '#b91c1c', // red-700
    },
    {
      key: 'bgGradientTo',
      type: 'color',
      label: 'Background Gradient End',
      default: '#991b1b', // red-900
    },
    {
      key: 'minHeight',
      type: 'select',
      label: 'Minimum Height',
      options: [
        { label: 'Auto', value: 'auto' },
        { label: 'Small (24rem)', value: 'min-h-96' },
        { label: 'Medium (32rem)', value: 'min-h-[32rem]' },
        { label: 'Large (40rem)', value: 'min-h-[40rem]' },
      ],
      default: 'min-h-96',
    },
    {
      key: 'showRegisterButton',
      type: 'boolean',
      label: 'Show Register Button',
      default: true,
    },
    {
      key: 'registerButtonUrl',
      type: 'text',
      label: 'Button Link URL',
      helpText: 'Custom URL for the register button. Leave empty to use default.',
      default: '',
    },
    {
      key: 'registerButtonText',
      type: 'text',
      label: 'Button Text',
      helpText: 'Custom button text. Leave empty to use default.',
      default: '',
    },
    {
      key: 'className',
      type: 'text',
      label: 'CSS Class',
    },
  ],
}

// Register schema
sectionSchemaRegistry.register(consultationFormSchema)

interface ConsultationFormProps {
  className?: string
  /** Override heading (uses locale if not provided) */
  heading?: string
  /** Heading text color */
  headingColor?: string
  /** Background gradient start color */
  bgGradientFrom?: string
  /** Background gradient end color */
  bgGradientTo?: string
  /** Minimum height */
  minHeight?: string
  /** Show register button */
  showRegisterButton?: boolean
  /** Custom URL for the register button */
  registerButtonUrl?: string
  /** Custom button text */
  registerButtonText?: string
}

export default function ConsultationForm({
  className,
  heading,
  headingColor,
  bgGradientFrom = '#b91c1c',
  bgGradientTo = '#991b1b',
  minHeight = 'min-h-96',
  showRegisterButton = true,
  registerButtonUrl,
  registerButtonText,
}: ConsultationFormProps): JSX.Element {
  const { text } = useLocale()
  const displayHeading = heading || text.CONSULTATION_FORM.TITLE

  return (
    <div
      className={cn('flex items-center justify-center', minHeight, className)}
      style={{
        background: `linear-gradient(to bottom right, ${bgGradientFrom}, ${bgGradientTo})`,
      }}
    >
      <Shell className="flex flex-col items-center justify-center py-4 sm:py-8 h-full">
        <h2
          className="text-white text-3xl md:text-4xl lg:text-5xl font-bold text-center py-6 max-w-3xl"
          style={headingColor ? { color: headingColor } : undefined}
          dangerouslySetInnerHTML={{ __html: sanitizeHtmlValue(displayHeading) }}
        />
        {showRegisterButton &&
          (registerButtonUrl ? (
            <Button variant="default" size="lg" className="text-base text-white" asChild>
              <a href={registerButtonUrl} target="_blank" rel="noopener noreferrer">
                {registerButtonText || text.MAIN.INTRODUCTION.HEADING_4}
              </a>
            </Button>
          ) : (
            <ButtonRegister />
          ))}
      </Shell>
    </div>
  )
}
